<script>
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'AISuggestionPanelSimple',
  components: {
    Modal,
    NextButton,
  },
  props: {
    conversationId: {
      type: Number,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      show: true,
      loading: true,
      analysis: null,
      recommendedTemplate: null,
      confidence: 0,
      preview: '',
    };
  },
  mounted() {
    console.log('AI Panel mounted for conversation:', this.conversationId);
    this.fetchAISuggestion();
  },
  methods: {
    async fetchAISuggestion() {
      this.loading = true;
      
      try {
        const response = await fetch('http://localhost:8000/api/suggest-template', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            conversation_id: this.conversationId,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        this.analysis = data.analysis;
        this.recommendedTemplate = data.recommended_template;
        this.confidence = Math.round(data.confidence * 100);
        this.preview = data.preview;
        
      } catch (error) {
        console.error('AI Suggestion Error:', error);
        this.analysis = {
          language: 'Unknown',
          customer_name: 'Unknown',
          order_number: null,
          sentiment: 'neutral'
        };
        this.preview = 'Error loading suggestion: ' + error.message;
      } finally {
        this.loading = false;
      }
    },
    
    applyTemplate() {
      console.log('Applying template:', this.preview);
      
      // Emit event to parent (ReplyBox) to insert the text
      this.$emit('apply-template', this.preview);
      
      // Close the modal
      this.show = false;
      this.onClose();
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col p-6">
      <!-- Header -->
      <div class="mb-6">
        <h2 class="text-xl font-semibold text-n-slate-12">AI Template Suggestion</h2>
        <p class="text-sm text-n-slate-11 mt-1">Intelligent template matching for this conversation</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="text-center">
          <div class="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p class="text-n-slate-11">Analyzing conversation...</p>
        </div>
      </div>

      <!-- Content -->
      <div v-else class="space-y-6">
        <!-- Analysis Section -->
        <div class="bg-n-slate-3 rounded-lg p-4">
          <h3 class="font-semibold text-n-slate-12 mb-3">Analysis</h3>
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span class="text-n-slate-11">Language:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ analysis?.language || 'Unknown' }}</span>
            </div>
            <div>
              <span class="text-n-slate-11">Customer:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ analysis?.customer_name || 'Unknown' }}</span>
            </div>
            <div>
              <span class="text-n-slate-11">Order:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ analysis?.order_number || 'Not found' }}</span>
            </div>
            <div>
              <span class="text-n-slate-11">Sentiment:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ analysis?.sentiment || 'Neutral' }}</span>
            </div>
          </div>
        </div>

        <!-- Recommendation Section -->
        <div v-if="recommendedTemplate" class="border border-n-slate-6 rounded-lg p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-semibold text-n-slate-12">Recommended Template</h3>
            <span class="text-sm font-semibold" :class="confidence >= 70 ? 'text-green-600' : 'text-yellow-600'">
              {{ confidence }}% match
            </span>
          </div>
          
          <div class="bg-n-slate-2 rounded p-3 mb-4">
            <p class="text-sm font-medium text-n-slate-11 mb-2">{{ recommendedTemplate.short_code }}</p>
            <div class="text-sm text-n-slate-12 whitespace-pre-wrap max-h-40 overflow-y-auto">
              {{ preview }}
            </div>
          </div>

          <div class="flex gap-2">
            <NextButton @click="applyTemplate" class="flex-1">
              Apply Template
            </NextButton>
            <NextButton @click="onClose" slate faded>
              Cancel
            </NextButton>
          </div>
        </div>

        <!-- No Match -->
        <div v-else class="text-center py-8">
          <p class="text-n-slate-11">No matching template found for this conversation.</p>
          <NextButton @click="onClose" slate faded class="mt-4">
            Close
          </NextButton>
        </div>
      </div>
    </div>
  </Modal>
</template>
